import React, { useState, useMemo, useEffect } from 'react';

import { DnsForwardingEditor } from '../components/dns-forwarding-editor/DnsForwardingEditor';
import {
  isValidIp,
  getNextRuleId,
  configToRules,
  rulesToConfig,
  validateRule,
} from 'Utils/dnsForwarding.utils';

import type {
  DnsRule,
  DnsForwardingEditorProps,
  RuleValidationError,
} from '../components/dns-forwarding-editor/DnsForwardingEditor.types';

export const DnsForwardingEditorContainer: React.FC<DnsForwardingEditorProps> = ({
  dnsConfig,
  onSave,
  isSubmitting,
}) => {
  const initialRules = useMemo(() => configToRules(dnsConfig), [dnsConfig]);
  const [rules, setRules] = useState<DnsRule[]>(initialRules);
  const [editErrors, setEditErrors] = useState<Record<number, RuleValidationError>>({});

  const [newDomain, setNewDomain] = useState('');
  const [newIps, setNewIps] = useState<string[]>([]);
  const [addError, setAddError] = useState<RuleValidationError | null>(null);

  useEffect(() => {
    setRules(initialRules);
    setEditErrors({});
  }, [initialRules]);

  const saveRules = (updatedRules: DnsRule[]) => {
    return onSave(rulesToConfig(updatedRules));
  };

  const handleNewDomainChange = (value: string) => {
    setNewDomain(value);
    if (addError?.domain) {
      setAddError((prev) => {
        if (!prev) return prev;
        const { domain: _, ...rest } = prev;
        return Object.keys(rest).length > 0 ? rest : null;
      });
    }
  };

  const handleNewIpsChange = (ips: string[]) => {
    const validIps = ips.filter((ip) => isValidIp(ip.trim()));
    const hasInvalid = validIps.length < ips.length;
    setNewIps(validIps);
    if (hasInvalid) {
      setAddError((prev) => ({ ...prev, ips: 'Invalid IP address' }));
    } else if (addError?.ips) {
      setAddError((prev) => {
        if (!prev) return prev;
        const { ips: _, ...rest } = prev;
        return Object.keys(rest).length > 0 ? rest : null;
      });
    }
  };

  const handleAddRule = () => {
    const newRule: DnsRule = { id: 0, domain: newDomain, ips: newIps };
    const error = validateRule(newRule, rules, -1);
    if (error) {
      setAddError(error);
      return;
    }
    const trimmedRule: DnsRule = {
      id: getNextRuleId(),
      domain: newDomain.trim(),
      ips: newIps.map((ip) => ip.trim()),
    };
    const updated = [...rules, trimmedRule];
    setRules(updated);
    saveRules(updated);
    setNewDomain('');
    setNewIps([]);
    setAddError(null);
  };

  const handleEditDomain = (index: number, value: string) => {
    setRules((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], domain: value };
      return updated;
    });
    setEditErrors((prev) => {
      const next = { ...prev };
      if (next[index]) {
        const { domain: _, ...rest } = next[index];
        next[index] = rest;
      }
      return next;
    });
  };

  const handleEditIps = (index: number, ips: string[]) => {
    const validIps = ips.filter((ip) => isValidIp(ip.trim()));
    const hasInvalid = validIps.length < ips.length;
    setRules((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ips: validIps };
      return updated;
    });
    setEditErrors((prev) => {
      const next = { ...prev };
      if (hasInvalid) {
        next[index] = { ...next[index], ips: 'Invalid IP address' };
      } else if (next[index]) {
        const { ips: _, ...rest } = next[index];
        next[index] = rest;
      }
      return next;
    });
  };

  const handleSaveRule = (index: number) => {
    const error = validateRule(rules[index], rules, index);
    if (error) {
      setEditErrors((prev) => ({ ...prev, [index]: error }));
      return;
    }
    setEditErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    saveRules(rules);
  };

  const handleDeleteRule = (index: number) => {
    const updated = rules.filter((_, i) => i !== index);
    setRules(updated);
    saveRules(updated);
    setEditErrors({});
  };

  return (
    <DnsForwardingEditor
      rules={rules}
      editErrors={editErrors}
      newDomain={newDomain}
      newIps={newIps}
      addError={addError}
      isSubmitting={isSubmitting}
      onNewDomainChange={handleNewDomainChange}
      onNewIpsChange={handleNewIpsChange}
      onAddRule={handleAddRule}
      onEditDomain={handleEditDomain}
      onEditIps={handleEditIps}
      onSaveRule={handleSaveRule}
      onDeleteRule={handleDeleteRule}
    />
  );
};
